import { useEffect, useMemo, useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import {
    getLeadsBaseAlcancavel, getLeadsDashboard, getLeadsDashboardPaginas,
} from '@/services/api/leads-dashboard';
import type {
    CanalLeads, JanelaBase, OrdenarPaginas, PeriodoLeads, TopPaginas,
} from '@/types/leadsDashboard';
import FiltrosLeads from './components/FiltrosLeads';
import { CardsBase, CardsReceita } from './components/CardsLeads';
import GraficoEntradaPerda from './components/GraficoEntradaPerda';
import GraficoBaseAlcancavel from './components/GraficoBaseAlcancavel';
import TabelaPaises from './components/TabelaPaises';
import RankingPaginas from './components/RankingPaginas';
import AvisoCampanhasDuplicadas from './components/AvisoCampanhasDuplicadas';
import ControlePaginas from './components/ControlePaginas';
import { n, pct, rotuloPeriodo } from './format';
import { useFiltroUsuarioAdmin } from '@/components/FiltroUsuarioAdmin';
import './leadsDashboard.css';

/**
 * Tela de Leads v7 (especificação completa).
 *
 * Sete blocos, nesta ordem — a ordem carrega hierarquia (§5): dinheiro
 * primeiro, depois base, depois recortes.
 *
 * Duas consultas de propósito (§6.1):
 *   - a consolidada carrega a tela e reage a período/país/página/janela;
 *   - a de páginas reage a canal/ordenação/top/busca, que é o controle mais
 *     mexido e o bloco mais caro. Sem separar, trocar a ordenação recarregaria
 *     receita, séries e países à toa.
 */

const hojeISO = () => new Date().toISOString().slice(0, 10);

export default function LeadsDashboard() {
    // ── Filtros globais (§3) ────────────────────────────────────────────────
    const [periodo, setPeriodo] = useState<PeriodoLeads>('hoje');
    const [de, setDe] = useState(hojeISO());
    const [ate, setAte] = useState(hojeISO());
    const [pais, setPais] = useState<string | null>(null);
    const [paginaId, setPaginaId] = useState<string | null>(null);
    // Admin vê a operação de todos os usuários e filtra por dono no servidor.
    const { usuarioId } = useFiltroUsuarioAdmin();

    // ── Controles locais de bloco ───────────────────────────────────────────
    // O período do topo é o pai: vale para a tela toda. Base e países têm
    // filtro próprio que mexe só no bloco; null = segue o pai. Trocar o
    // período do topo devolve os dois ao pai.
    const [janelaBase, setJanelaBase] = useState<JanelaBase | null>(30);
    const [periodoPaises, setPeriodoPaises] = useState<PeriodoLeads | null>(null);
    const soltarFilhos = () => { setJanelaBase(null); setPeriodoPaises(null); };
    const [canal, setCanal] = useState<CanalLeads>('todos');
    const [top, setTop] = useState<TopPaginas>(10);
    const [ordenarPor, setOrdenarPor] = useState<OrdenarPaginas>('captados');
    const [busca, setBusca] = useState('');
    const [buscaDebounced, setBuscaDebounced] = useState('');

    useEffect(() => {
        const t = setTimeout(() => setBuscaDebounced(busca.trim()), 300);
        return () => clearTimeout(t);
    }, [busca]);

    const paramsBase = {
        periodo,
        de: periodo === 'custom' ? de : undefined,
        ate: periodo === 'custom' ? ate : undefined,
        pais,
        pagina_id: paginaId,
        user_id: usuarioId,
    };

    const {
        data, isLoading, isError, error, refetch,
    } = useQuery({
        queryKey: ['leads-dashboard', paramsBase],
        queryFn: () => getLeadsDashboard({ ...paramsBase, canal, top, ordenar_por: ordenarPor }),
        staleTime: 1000 * 60 * 2,
        placeholderData: keepPreviousData,
    });

    // Bloco de páginas: recarrega sozinho quando muda canal/ordem/top/busca.
    const {
        data: blocoPaginas, isFetching: carregandoPaginas,
    } = useQuery({
        queryKey: ['leads-dashboard-paginas', paramsBase, canal, top, ordenarPor, buscaDebounced],
        queryFn: () => getLeadsDashboardPaginas({
            ...paramsBase, canal, top, ordenar_por: ordenarPor,
            busca: buscaDebounced || null,
        }),
        staleTime: 1000 * 60 * 2,
        enabled: !!data,
        placeholderData: keepPreviousData,
    });

    const { data: baseFilho } = useQuery({
        queryKey: ['leads-dashboard-base', janelaBase, pais, paginaId, usuarioId],
        queryFn: () => getLeadsBaseAlcancavel({
            janela_base: janelaBase!, pais, pagina_id: paginaId, user_id: usuarioId,
        }),
        staleTime: 1000 * 60 * 2,
        enabled: janelaBase != null,
        placeholderData: keepPreviousData,
    });
    const baseAlcancavel = janelaBase != null ? baseFilho : data?.base_alcancavel;

    // Com período próprio, a tabela de países busca o consolidado daquele
    // período e usa só o bloco de países (é a mesma conta, com cache).
    const paramsPaises = { ...paramsBase, periodo: periodoPaises!, de: undefined, ate: undefined };
    const { data: paisesFilho } = useQuery({
        queryKey: ['leads-dashboard', paramsPaises],
        queryFn: () => getLeadsDashboard({ ...paramsPaises, canal, top, ordenar_por: ordenarPor }),
        staleTime: 1000 * 60 * 2,
        enabled: periodoPaises != null,
        placeholderData: keepPreviousData,
    });
    const dadosPaises = periodoPaises != null ? paisesFilho : data;

    const paginas = blocoPaginas?.paginas ?? data?.paginas;
    const controle = blocoPaginas?.controle_paginas ?? data?.controle_paginas;

    const rotulo = useMemo(
        () => rotuloPeriodo(periodo, data?.filtros.dias ?? 1),
        [periodo, data?.filtros.dias]
    );

    const opcoesPaises = useMemo(
        () => (data?.paises.itens ?? []).map(p => ({ codigo: p.codigo, nome: p.nome })),
        [data?.paises.itens]
    );
    const opcoesPaginas = useMemo(
        () => (data?.paginas.itens ?? []).map(p => ({ id: p.id, nome: p.nome, pais: p.pais })),
        [data?.paginas.itens]
    );

    const nomePais = pais
        ? data?.paises.itens.find(p => p.codigo === pais)?.nome
        : undefined;
    const nomePagina = paginaId
        ? (paginas?.itens.find(p => p.id === paginaId)?.nome
            ?? paginas?.fixada?.nome)
        : undefined;

    const limpar = () => {
        setPeriodo('hoje');
        setJanelaBase(30);
        setPeriodoPaises(null);
        setPais(null);
        setPaginaId(null);
    };

    // §8: erro de carregamento diz o que falhou e oferece nova tentativa, sem
    // derrubar a tela inteira.
    if (isError) {
        return (
            <div className="lx">
                <div className="top">
                    <div><h1>Leads</h1></div>
                </div>
                <div className="aviso aviso-red">
                    <div>
                        <strong>Não foi possível carregar a tela.</strong>{' '}
                        {error instanceof Error ? error.message : 'Erro desconhecido.'}
                        <div style={{ marginTop: 6 }}>
                            <button type="button" className="clearall" onClick={() => refetch()}>
                                Tentar de novo
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const moeda = data?.meta.moeda ?? 'USD';

    return (
        <div className="lx">
            <div className="top">
                <div>
                    <h1>Leads</h1>
                    <div className="sub">
                        Retenção via Messenger
                        {controle && ` · ${controle.cadastradas} páginas ativas`}
                        {data && ` · ${data.paises._total_itens} países`}
                    </div>
                </div>
                {data && (() => {
                    // A HORA DO JOB, não a da resposta.
                    //
                    // Antes mostrava `gerado_em`, que é quando a API respondeu
                    // — sempre "agora", sempre tranquilizador, e sem relação
                    // com a idade do dado. O dia corrente é materializado de
                    // hora em hora: às 12:19 o que está na tela foi calculado
                    // às 11:50.
                    //
                    // Sem `dados_atualizados_em` (API antiga), não mostra nada:
                    // rótulo nenhum é melhor que um que engana.
                    const em = data.meta.dados_atualizados_em;
                    if (!em) return null;
                    const d = new Date(em);
                    const minutos = Math.max(0, Math.round((Date.now() - d.getTime()) / 60000));
                    const hora = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
                    return (
                        <div
                            className="sub num"
                            title={`Materializado em ${d.toLocaleString('pt-BR')} · resposta da API às ${new Date(data.meta.gerado_em).toLocaleTimeString('pt-BR')}`}
                        >
                            Dados de {hora}
                            <small style={{ marginLeft: 6, fontWeight: 500 }}>
                                {minutos < 1 ? 'agora' : minutos < 60 ? `há ${minutos} min` : `há ${Math.floor(minutos / 60)}h${String(minutos % 60).padStart(2, '0')}`}
                            </small>
                        </div>
                    );
                })()}
            </div>

            <FiltrosLeads
                periodo={periodo} de={de} ate={ate} pais={pais} paginaId={paginaId}
                paises={opcoesPaises} paginas={opcoesPaginas}
                onUsuario={() => { setPais(null); setPaginaId(null); }}
                onPeriodo={p => { setPeriodo(p); soltarFilhos(); }}
                onDe={d => { setDe(d); soltarFilhos(); }}
                onAte={a => { setAte(a); soltarFilhos(); }}
                onPais={setPais} onPagina={setPaginaId} onLimpar={limpar}
            />

            {/* Rótulos de confiança: a tela precisa dizer o que é medido e o que
                é derivado, em vez de exibir tudo com o mesmo peso. */}
            {data && (
                <>
                    {data.meta.invariantes_violados.length > 0 && (
                        <div className="aviso aviso-red">
                            <div>
                                <strong>Inconsistência nos dados.</strong> Os números abaixo
                                violam regras que deveriam sempre valer — é bug de dado, não de
                                exibição:
                                <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                                    {data.meta.invariantes_violados.map(v => <li key={v}>{v}</li>)}
                                </ul>
                            </div>
                        </div>
                    )}
                    <div className="aviso aviso-amber">
                        <div>
                            <strong>Como ler estes números.</strong>{' '}
                            Receita é <strong>medida</strong> por página: a origem devolve por
                            utm_campaign e cada página tem a sua no cadastro. A de flow é própria
                            da página; a de broadcast é medida para o grupo de páginas que
                            compartilham a campanha e dividida entre elas.
                            {typeof data.meta.receita_sem_pagina_pct === 'number'
                                && data.meta.receita_sem_pagina_pct > 0 && (
                                <> Hoje {pct(data.meta.receita_sem_pagina_pct, 1)} da receita não
                                casou com página nenhuma e fica fora do ranking.</>
                            )}
                            {' '}O país do lead é herdado da página a que ele pertence.
                            {(data.meta.paginas_pausadas_excluidas ?? 0) > 0 && (
                                <> Os números cobrem só páginas <strong>ativas</strong>:{' '}
                                {n(data.meta.paginas_pausadas_excluidas!)} pausada(s) ficaram de
                                fora, porque página desligada não dispara e o estoque dela inflaria
                                todo denominador.</>
                            )}
                        </div>
                    </div>
                    {/* Divide campanha = divide receita. A tela tem que dizer
                        isso antes de alguém tomar decisão pelo número. */}
                    <AvisoCampanhasDuplicadas
                        paginaId={paginaId}
                        nomePagina={opcoesPaginas.find(p => p.id === paginaId)?.nome}
                    />
                </>
            )}

            {/* §8: esqueleto por bloco, nunca spinner de tela cheia. Receita e
                base carregam primeiro; páginas por último. */}
            {isLoading || !data ? (
                <>
                    <div className="kpis3">
                        {Array.from({ length: 3 }).map((_, i) => <div key={i} className="skel skel-kpi" />)}
                    </div>
                    <div className="kpis">
                        {Array.from({ length: 6 }).map((_, i) => <div key={i} className="skel skel-kpi" />)}
                    </div>
                    <div className="skel skel-card" style={{ marginBottom: 12 }} />
                    <div className="skel skel-card" />
                </>
            ) : (
                <>
                    <CardsReceita
                        receita={data.receita} rotulo={rotulo} moeda={moeda}
                        indisponivel={data.meta.receita_indisponivel}
                    />
                    <CardsBase
                        base={data.base} receita={data.receita} rotulo={rotulo} moeda={moeda}
                    />
                    <GraficoEntradaPerda
                        serieDiaria={data.serie_diaria}
                        serieHoraria={data.serie_horaria}
                        melhorPior={data.melhor_pior_dia}
                        base={data.base}
                        rotulo={rotulo}
                        dias={data.filtros.dias}
                        nomePais={nomePais}
                        nomePagina={nomePagina}
                    />
                    {baseAlcancavel ? (
                        <GraficoBaseAlcancavel
                            dados={baseAlcancavel}
                            janela={janelaBase}
                            onJanela={setJanelaBase}
                        />
                    ) : <div className="skel skel-card" style={{ marginBottom: 12 }} />}
                    {dadosPaises ? (
                        <TabelaPaises
                            itens={dadosPaises.paises.itens}
                            paisSelecionado={pais}
                            periodo={periodoPaises ?? periodo}
                            rotulo={rotuloPeriodo(periodoPaises ?? periodo, dadosPaises.filtros.dias)}
                            moeda={moeda}
                            onPais={c => { setPais(c); setPaginaId(null); }}
                            onPeriodo={setPeriodoPaises}
                        />
                    ) : <div className="skel skel-card" style={{ marginBottom: 12 }} />}
                    {paginas && (
                        <RankingPaginas
                            itens={paginas.itens}
                            fixada={paginas.fixada}
                            paginaSelecionada={paginaId}
                            ordenarPor={ordenarPor}
                            canal={canal}
                            top={top}
                            busca={busca}
                            rotulo={rotulo}
                            nomePais={nomePais}
                            moeda={moeda}
                            carregando={carregandoPaginas}
                            onOrdenar={setOrdenarPor}
                            onCanal={setCanal}
                            onTop={setTop}
                            onBusca={setBusca}
                            onPagina={setPaginaId}
                        />
                    )}
                    {controle && (
                        <ControlePaginas
                            dados={controle}
                            rotulo={rotulo}
                            paginaSelecionada={paginaId}
                            onPagina={setPaginaId}
                        />
                    )}
                </>
            )}
        </div>
    );
}
